import { useId, useRef, useState } from "react";
import {
  ACTION_CHECK_IN_NOTE_MAX,
  ACTION_MAX_WEEKS,
  ApiError,
  type CheckInChannel,
  type CheckInOutcome,
  type PatientAction,
  type PatientActionCheckInCreate,
} from "@ferrivia/api-contract";
import { useTranslation } from "@ferrivia/i18n";
import { Button, RadioChip, Textarea, TextInput } from "@ferrivia/ui";
import { CHECK_IN_CHANNELS, useActionLabels } from "../../actions/kinds";
import { checkInDateFromWeeks, toIsoDate } from "../../actions/plan";
import type { StaffProfileState } from "../../hooks/useStaffProfile";
import { ActionDrawerShell } from "../ActionDrawer";
import d from "../ActionDrawer/ActionDrawer.module.css";
import s from "./ActionCheckIn.module.css";

export interface ActionCheckInProps {
  container: HTMLElement | null;
  action: PatientAction;
  staff: StaffProfileState;
  onClose: () => void;
  onChooseChange: () => void;
  onSave: (input: PatientActionCheckInCreate) => Promise<unknown>;
}

type Choice = CheckInOutcome | "change";

function parseWeeks(text: string): number | null {
  const n = Number(text);
  return text.trim() !== "" && Number.isInteger(n) && n >= 1 && n <= ACTION_MAX_WEEKS ? n : null;
}

export function ActionCheckIn({ container, action, staff, onClose, onChooseChange, onSave }: ActionCheckInProps) {
  const { t } = useTranslation("clinic");
  const { channelLabel, titleLabel } = useActionLabels();
  const uid = useId();
  const [today] = useState(() => new Date());
  const [choice, setChoice] = useState<CheckInOutcome | null>(null);
  const [weeksText, setWeeksText] = useState("");
  const [nextOn, setNextOn] = useState("");
  const [dateEdited, setDateEdited] = useState(false);
  const [channel, setChannel] = useState<CheckInChannel | null>(null);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [errorKey, setErrorKey] = useState<string | null>(null);

  const profile = staff.status === "success" ? staff.profile : null;
  const titleMissing = profile !== null && profile.title === null;
  const isContinue = choice === "continue";
  const canSave =
    choice !== null &&
    (!isContinue || (nextOn !== "" && channel !== null)) &&
    staff.status !== "loading" &&
    !titleMissing &&
    !saving;

  const saveRef = useRef<() => void>(() => undefined);

  function pick(next: Choice) {
    if (next === "change") {
      onChooseChange();
      return;
    }
    setChoice(next);
  }

  function onWeeksChange(value: string) {
    setWeeksText(value);
    if (!dateEdited) {
      const n = parseWeeks(value);
      setNextOn(n === null ? "" : checkInDateFromWeeks(today, n));
    }
  }

  async function save() {
    if (!canSave || choice === null) {
      return;
    }
    const input: PatientActionCheckInCreate = {
      outcome: choice,
      ...(note.trim() !== "" ? { note: note.trim() } : {}),
      ...(isContinue && channel !== null ? { nextCheckInOn: nextOn, nextCheckInChannel: channel } : {}),
    };
    setSaving(true);
    setErrorKey(null);
    try {
      await onSave(input);
      onClose();
    } catch (err) {
      setErrorKey(err instanceof ApiError ? err.code : "network");
      setSaving(false);
    }
  }
  saveRef.current = () => void save();

  function onKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      saveRef.current();
    }
  }

  const errorText =
    errorKey === null
      ? null
      : errorKey === "staff_profile_incomplete"
        ? t("actionDrawer.errorProfile", "Your professional title isn't set — ask Ville.")
        : errorKey === "invalid_check_in_date"
          ? t("actionDrawer.errorDate", "Pick a check-in date that hasn't passed.")
          : errorKey === "action_not_active"
            ? t("actionDrawer.errorNotActive", "This action has already ended or been changed.")
            : t("actionDrawer.errorFailed", "Couldn't save. Try again.");

  const outcomes: { value: Choice; label: string; sub: string }[] = [
    { value: "continue", label: t("actionCheckIn.continue", "Continue"), sub: t("actionCheckIn.continueSub", "Same action, new check-in date") },
    { value: "change", label: t("actionCheckIn.change", "Change"), sub: t("actionCheckIn.changeSub", "Close this, write its successor") },
    { value: "stop", label: t("actionCheckIn.stop", "Stop"), sub: t("actionCheckIn.stopSub", "Ended before its time") },
    { value: "complete", label: t("actionCheckIn.complete", "Complete"), sub: t("actionCheckIn.completeSub", "Ran its course") },
  ];

  const footer = (
    <>
      {titleMissing ? (
        <p className={d.blocked} role="alert">
          {t("actionDrawer.titleMissing", "Your professional title isn't set — ask Ville.")}
        </p>
      ) : null}
      {errorText ? (
        <p className={d.error} role="alert">
          {errorText}
        </p>
      ) : null}
      {profile?.name && profile.title ? (
        <span className={d.recordedAs}>
          {t("actionDrawer.recordedAs", "Recorded as")} <b>{`${profile.name}, ${titleLabel(profile.title)}`}</b>
        </span>
      ) : null}
      <div className={d.footButtons}>
        <Button variant="secondary" onClick={onClose}>
          {t("actionDrawer.cancel", "Cancel")}
        </Button>
        <Button onClick={() => void save()} disabled={!canSave}>
          {t("actionCheckIn.save", "Save check-in")}
        </Button>
      </div>
    </>
  );

  return (
    <ActionDrawerShell
      container={container}
      title={t("actionCheckIn.title", { title: action.title, defaultValue: "Check-in: {{title}}" })}
      onClose={onClose}
      footer={footer}
      onKeyDown={onKeyDown}
    >
      <div className={d.field}>
        <span id={`${uid}-outcome`} className={d.label}>
          {t("actionCheckIn.outcome", "Outcome")}
        </span>
        <div role="radiogroup" aria-labelledby={`${uid}-outcome`} className={s.outcomes}>
          {outcomes.map((o) => (
            <RadioChip
              key={o.value}
              name={`${uid}-outcome`}
              className={s.card}
              checked={choice === o.value}
              onChange={() => pick(o.value)}
            >
              <span className={s.cardLabel}>{o.label}</span>
              <span className={s.cardSub}>{o.sub}</span>
            </RadioChip>
          ))}
        </div>
      </div>

      {isContinue ? (
        <div className={d.field}>
          <span id={`${uid}-next`} className={d.label}>
            {t("actionCheckIn.nextCheckIn", "Next check-in")}
          </span>
          <div className={d.chips}>
            <span className={d.inline}>
              <TextInput
                type="number"
                inputMode="numeric"
                min={1}
                max={ACTION_MAX_WEEKS}
                className={d.weeks}
                aria-label={t("actionCheckIn.moreWeeks", "More weeks")}
                value={weeksText}
                onChange={(event) => onWeeksChange(event.target.value)}
              />
              {t("actionCheckIn.moreWeeksUnit", "more weeks →")}
            </span>
          </div>
          <TextInput
            type="date"
            aria-labelledby={`${uid}-next`}
            value={nextOn}
            min={toIsoDate(today)}
            onChange={(event) => {
              setNextOn(event.target.value);
              setDateEdited(event.target.value !== "");
            }}
          />
          <div role="radiogroup" aria-label={t("actionDrawer.channel", "Check-in channel")} className={d.chips}>
            {CHECK_IN_CHANNELS.map((option) => (
              <RadioChip
                key={option}
                name={`${uid}-channel`}
                checked={channel === option}
                onChange={() => setChannel(option)}
              >
                {channelLabel(option)}
              </RadioChip>
            ))}
          </div>
        </div>
      ) : null}

      <div className={d.field}>
        <label htmlFor={`${uid}-note`} className={d.label}>
          {t("actionCheckIn.note", "Why — in your words")}
        </label>
        <Textarea
          id={`${uid}-note`}
          value={note}
          rows={3}
          maxHeight={200}
          maxLength={ACTION_CHECK_IN_NOTE_MAX}
          onChange={(event) => setNote(event.target.value)}
        />
      </div>
    </ActionDrawerShell>
  );
}
